import React, { useMemo, useState } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
  LineChart,
  Line,
  ComposedChart,
  Cell,
} from "recharts";
import { ChevronRight, ArrowLeft, AlertTriangle, Wrench, Activity, Gauge, Leaf } from "lucide-react";
import { PageHeader, KpiCard, SectionCard } from "@/components/Shared";
import {
  monthlyOps,
  weeklyOps,
  getDailyOps,
  equipmentList,
  equipmentByCategory,
  maintenanceTimeline,
  bottlenecks,
} from "@/data/mineData";
import { employmentEquity, smeParticipation, esgScores, rehabilitation } from "@/data/geoEsgData";

const tooltipStyle = {
  contentStyle: { backgroundColor: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 12 },
};

const statusStyles = {
  Active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Idle: "bg-slate-100 text-slate-700 border-slate-200",
  Maintenance: "bg-amber-50 text-amber-700 border-amber-200",
};

const categoryColor = {
  Haulage: "#0F172A",
  Excavation: "#B45309",
  Loading: "#1E40AF",
  Drilling: "#DC2626",
  Processing: "#475569",
  Conveying: "#059669",
  Power: "#7C3AED",
  Service: "#94A3B8",
};

function fmt(n) {
  return n?.toLocaleString();
}

// ---- Drill-down chart: Month → Week → Day ----
function DrillDownChart() {
  const [level, setLevel] = useState("month"); // month | week | day
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedWeek, setSelectedWeek] = useState(null);

  const data = useMemo(() => {
    if (level === "month") return monthlyOps;
    if (level === "week") return weeklyOps[selectedMonth] || [];
    if (level === "day") {
      const w = (weeklyOps[selectedMonth] || []).find((x) => x.period === selectedWeek);
      return w ? getDailyOps(w) : [];
    }
    return [];
  }, [level, selectedMonth, selectedWeek]);

  const handleBarClick = (datum) => {
    if (!datum) return;
    const period = datum.period;
    if (level === "month") {
      setSelectedMonth(period);
      setLevel("week");
    } else if (level === "week") {
      setSelectedWeek(period);
      setLevel("day");
    }
  };

  const goBack = () => {
    if (level === "day") {
      setLevel("week");
      setSelectedWeek(null);
    } else if (level === "week") {
      setLevel("month");
      setSelectedMonth(null);
    }
  };

  const breadcrumb = [
    { label: "Year 2024", active: level === "month" },
    selectedMonth && { label: selectedMonth, active: level === "week" },
    selectedWeek && { label: selectedWeek, active: level === "day" },
  ].filter(Boolean);

  return (
    <SectionCard
      title="Production Deep-Dive"
      description={
        level === "month"
          ? "Click a month bar to drill into weeks · then days"
          : level === "week"
          ? `Weekly breakdown of ${selectedMonth} — click a week to see days`
          : `Daily breakdown of ${selectedMonth} ${selectedWeek}`
      }
      action={
        level !== "month" && (
          <button
            onClick={goBack}
            data-testid="drilldown-back"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-700 hover:text-slate-900 px-2.5 py-1 rounded-md hover:bg-slate-100"
          >
            <ArrowLeft size={14} /> Back
          </button>
        )
      }
    >
      <div className="flex items-center gap-2 mb-3 text-xs" data-testid="drilldown-breadcrumb">
        {breadcrumb.map((b, i) => (
          <React.Fragment key={i}>
            <span className={b.active ? "font-semibold text-slate-900" : "text-slate-500"}>
              {b.label}
            </span>
            {i < breadcrumb.length - 1 && <ChevronRight size={12} className="text-slate-400" />}
          </React.Fragment>
        ))}
      </div>

      <div className="h-72" data-testid="drilldown-chart">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
            <XAxis dataKey="period" stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
            <YAxis yAxisId="left" stroke="#64748b" fontSize={11} tickLine={false} axisLine={false}
              tickFormatter={(v) => `${v / 1000}k`} />
            <YAxis yAxisId="right" orientation="right" stroke="#64748b" fontSize={11} tickLine={false} axisLine={false}
              tickFormatter={(v) => `${v}%`} domain={[70, 100]} />
            <Tooltip {...tooltipStyle} formatter={(v, n) => n === "Uptime %" ? `${v}%` : fmt(v)} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar yAxisId="left" dataKey="production" fill="#0F172A" name="Production (t)" radius={[4, 4, 0, 0]}
              onClick={level !== "day" ? handleBarClick : undefined}
              cursor={level !== "day" ? "pointer" : "default"}>
              {data.map((d, i) => (
                <Cell key={i}
                  fill={d.production >= d.target ? "#0F172A" : "#475569"} />
              ))}
            </Bar>
            <Bar yAxisId="left" dataKey="target" fill="#FCD34D" name="Target" opacity={0.5} radius={[4, 4, 0, 0]}
              onClick={level !== "day" ? handleBarClick : undefined}
              cursor={level !== "day" ? "pointer" : "default"} />
            <Line yAxisId="right" type="monotone" dataKey="uptime" stroke="#B45309" strokeWidth={2.5}
              dot={{ r: 3 }} name="Uptime %" />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </SectionCard>
  );
}

// ---- Maintenance Timeline (Gantt-like) ----
function MaintenanceTimeline() {
  const sorted = [...maintenanceTimeline].sort((a, b) => new Date(a.date) - new Date(b.date));
  const minDate = new Date(sorted[0].date);
  const maxDate = new Date(sorted[sorted.length - 1].date);
  const totalMs = maxDate - minDate;

  const monthMarks = [];
  const cur = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
  while (cur <= maxDate) {
    const offset = ((cur - minDate) / totalMs) * 100;
    monthMarks.push({
      label: cur.toLocaleDateString("en-US", { month: "short" }),
      offset: Math.max(0, Math.min(100, offset)),
    });
    cur.setMonth(cur.getMonth() + 1);
  }

  // Group by asset
  const grouped = sorted.reduce((acc, e) => {
    if (!acc[e.asset]) acc[e.asset] = [];
    acc[e.asset].push(e);
    return acc;
  }, {});

  return (
    <SectionCard
      title="Maintenance Timeline"
      description="Planned & unplanned interventions, last 6 months"
      action={<Wrench size={16} className="text-slate-700" />}
    >
      <div className="relative" data-testid="maintenance-timeline">
        {/* axis */}
        <div className="ml-44 relative h-6 mb-2 border-b border-slate-200">
          {monthMarks.map((m, i) => (
            <div key={i} className="absolute top-0 -translate-x-1/2 text-[10px] text-slate-500 font-semibold uppercase tracking-wider"
              style={{ left: `${m.offset}%` }}>
              {m.label}
              <div className="absolute left-1/2 top-4 w-px h-2 bg-slate-300" />
            </div>
          ))}
        </div>

        {Object.entries(grouped).map(([asset, events]) => (
          <div key={asset} className="flex items-center mb-2 group">
            <div className="w-44 pr-3 text-xs text-slate-700 truncate font-medium" title={asset}>
              {asset}
            </div>
            <div className="flex-1 relative h-6 bg-slate-50 rounded">
              {events.map((e, i) => {
                const offset = ((new Date(e.date) - minDate) / totalMs) * 100;
                const width = Math.max(1.5, (e.durationHrs / 24) / 180 * 100); // duration days as % of total
                return (
                  <div
                    key={i}
                    className={`absolute top-0.5 bottom-0.5 rounded ${e.type === "planned" ? "bg-slate-800" : "bg-amber-600"}`}
                    style={{ left: `${offset}%`, width: `${width}%`, minWidth: "4px" }}
                    title={`${e.date} · ${e.type} · ${e.durationHrs}h · ${e.note}`}
                  />
                );
              })}
            </div>
          </div>
        ))}

        <div className="flex items-center gap-4 mt-4 pt-3 border-t border-slate-100 text-xs">
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded bg-slate-800" />
            <span className="text-slate-600">Planned</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded bg-amber-600" />
            <span className="text-slate-600">Unplanned</span>
          </div>
          <span className="ml-auto text-slate-500">Hover bars for detail · {sorted.length} events</span>
        </div>
      </div>
    </SectionCard>
  );
}

// ---- Asset Register Drawer ----
function AssetDetailDrawer({ asset, onClose }) {
  if (!asset) return null;
  const isHrs = asset.lifeKm === 0 || !asset.lifeKm;
  const mileageLabel = isHrs ? `${fmt(asset.lifeHrs)} hrs` : `${fmt(asset.lifeKm)} km`;
  const sinceServiceLabel = isHrs
    ? `${fmt(asset.hrsSinceService)} hrs`
    : `${fmt(asset.kmSinceService)} km`;

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex justify-end" onClick={onClose} data-testid="asset-drawer">
      <div className="w-full max-w-md bg-white h-full overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-slate-200 sticky top-0 bg-white z-10">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold">{asset.category}</div>
              <h3 className="font-display text-lg font-semibold text-slate-900 mt-1">{asset.type}</h3>
              <div className="text-xs text-slate-500 font-mono mt-1">{asset.id}</div>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-xl leading-none">×</button>
          </div>
        </div>

        <div className="p-6 space-y-5">
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-slate-50 rounded-md p-3 border border-slate-100">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Status</div>
              <div className="mt-1">
                <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${statusStyles[asset.status]}`}>
                  {asset.status}
                </span>
              </div>
            </div>
            <div className="bg-slate-50 rounded-md p-3 border border-slate-100">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Health Score</div>
              <div className="font-display text-xl font-bold text-slate-900 mt-1">{asset.healthScore}<span className="text-xs text-slate-500">/100</span></div>
            </div>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Lifetime usage</div>
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="text-slate-600">Total {isHrs ? "hours" : "kilometres"}</span>
              <span className="font-display font-semibold text-slate-900">{mileageLabel}</span>
            </div>
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="text-slate-600">Since last service</span>
              <span className="text-amber-700 font-semibold">{sinceServiceLabel}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-600">Commissioned</span>
              <span className="text-slate-900">{asset.commissioned}</span>
            </div>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Service schedule</div>
            <div className="flex items-center justify-between text-sm py-2 border-b border-slate-100">
              <span className="text-slate-600">Last service</span>
              <span className="text-slate-900">{asset.lastService}</span>
            </div>
            <div className="flex items-center justify-between text-sm py-2">
              <span className="text-slate-600">Next service due</span>
              <span className="text-slate-900 font-semibold">{asset.nextService}</span>
            </div>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Uptime (last 30 days)</div>
            <div className="flex items-center gap-3">
              <div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden">
                <div className={`h-full ${asset.uptime >= 85 ? "bg-emerald-600" : asset.uptime > 0 ? "bg-amber-600" : "bg-slate-400"}`}
                  style={{ width: `${asset.uptime || 0}%` }} />
              </div>
              <span className="font-display text-lg font-bold text-slate-900 w-16 text-right">
                {asset.uptime ? `${asset.uptime}%` : "—"}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Operations() {
  const [filter, setFilter] = useState("All");
  const [selectedAsset, setSelectedAsset] = useState(null);

  const active = equipmentList.filter((e) => e.status === "Active").length;
  const idle = equipmentList.filter((e) => e.status !== "Active").length;
  const avgUptime = (
    equipmentList.filter((e) => e.uptime > 0).reduce((s, e) => s + e.uptime, 0) /
    equipmentList.filter((e) => e.uptime > 0).length
  ).toFixed(1);

  const categories = ["All", ...Array.from(new Set(equipmentList.map((e) => e.category)))];
  const filtered = filter === "All" ? equipmentList : equipmentList.filter((e) => e.category === filter);

  return (
    <div data-testid="page-operations">
      <PageHeader eyebrow="Operations & Capacity" title="Operational Performance" subtitle="Drill from year → month → week → day. Click any asset for mileage, hours, and service history." />

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
        <KpiCard testid="kpi-equip-count" label="Total Equipment" value={equipmentList.length.toString()} unit="units · 8 categories" />
        <KpiCard testid="kpi-active" label="Active vs Idle" value={`${active}/${idle}`} unit="active / inactive" accent="green" />
        <KpiCard testid="kpi-avg-uptime" label="Average Uptime" value={`${avgUptime}%`} unit="across active fleet" />
        <KpiCard testid="kpi-maintenance" label="Maintenance Events" value="19" unit="last 30 days" accent="amber" />
      </div>

      {/* ESG / Workforce strip — cross-link to Geological & ESG Intelligence */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 mb-6 shadow-sm" data-testid="ops-esg-strip">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Leaf size={14} className="text-emerald-700" />
            <span className="text-[10px] uppercase tracking-[0.18em] text-emerald-700 font-semibold">ESG · Workforce · SLP</span>
          </div>
          <a href="/geo-esg" className="text-[11px] font-semibold text-amber-700 hover:underline" data-testid="ops-link-geo-esg">
            Open full ESG view →
          </a>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <EsgStat label="Local workforce" value={`${employmentEquity.find(e => e.metric === "Local Limpopo workforce")?.actualPct || 0}%`} sub="Limpopo" tone="emerald" />
          <EsgStat label="HDSA in core skills" value={`${employmentEquity.find(e => e.metric === "HDSA in core skills")?.actualPct || 0}%`} sub="vs 60% target" tone="emerald" />
          <EsgStat label="Local procurement" value={`${smeParticipation.localProcurementPct}%`} sub={`${smeParticipation.bbbeeCompliantSuppliers} BBBEE suppliers`} tone="amber" />
          <EsgStat label="ESG composite" value={esgScores.composite.toString()} sub={`Rehab ${rehabilitation.progressPct.toFixed(0)}% complete`} tone="slate" />
        </div>
      </div>

      {/* Drill-down */}
      <div className="mb-6">
        <DrillDownChart />
      </div>

      {/* Capex by category + Avg uptime */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mb-6">
        <SectionCard title="Capex by Category" description="ZAR millions invested per asset class" action={<Gauge size={16} className="text-slate-700" />}>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={equipmentByCategory} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="category" stroke="#64748b" fontSize={10} tickLine={false} axisLine={false} angle={-15} textAnchor="end" height={50} />
                <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `R${v}m`} />
                <Tooltip {...tooltipStyle} formatter={(v) => `R${v.toLocaleString()}m`} />
                <Bar dataKey="capex" radius={[4, 4, 0, 0]}>
                  {equipmentByCategory.map((c, i) => <Cell key={i} fill={categoryColor[c.category]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </SectionCard>

        <SectionCard title="Avg Uptime by Category" description="Note: Excavation/Drilling include idle and maintenance units" action={<Activity size={16} className="text-slate-700" />}>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={equipmentByCategory} margin={{ top: 8, right: 8, left: -8, bottom: 0 }} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                <XAxis type="number" stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                <YAxis type="category" dataKey="category" stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} width={90} />
                <Tooltip {...tooltipStyle} formatter={(v) => `${v}%`} />
                <Bar dataKey="avgUptime" radius={[0, 4, 4, 0]}>
                  {equipmentByCategory.map((c, i) => (
                    <Cell key={i} fill={c.avgUptime > 80 ? "#059669" : c.avgUptime > 60 ? "#D97706" : "#DC2626"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </SectionCard>
      </div>

      {/* Maintenance Timeline */}
      <div className="mb-6">
        <MaintenanceTimeline />
      </div>

      {/* Asset Register */}
      <SectionCard title="Asset Register" description={`Click any row for full asset detail · showing ${filtered.length} of ${equipmentList.length}`}>
        <div className="flex flex-wrap items-center gap-2 mb-4">
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setFilter(c)}
              data-testid={`asset-filter-${c.toLowerCase()}`}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                filter === c
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"
              }`}
            >
              {c}
              {c !== "All" && (
                <span className="ml-1.5 text-[10px] opacity-70">
                  ({equipmentList.filter((e) => e.category === c).length})
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="overflow-x-auto" data-testid="asset-register-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="py-3 pr-4 font-semibold">Asset ID</th>
                <th className="py-3 pr-4 font-semibold">Type</th>
                <th className="py-3 pr-4 font-semibold">Status</th>
                <th className="py-3 pr-4 font-semibold">Mileage / Hours</th>
                <th className="py-3 pr-4 font-semibold">Uptime</th>
                <th className="py-3 pr-4 font-semibold">Health</th>
                <th className="py-3 pr-4 font-semibold">Next service</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((e) => {
                const isHrs = !e.lifeKm;
                return (
                  <tr key={e.id}
                    onClick={() => setSelectedAsset(e)}
                    data-testid={`asset-row-${e.id}`}
                    className="border-b border-slate-100 hover:bg-slate-50 transition-colors cursor-pointer">
                    <td className="py-3 pr-4 font-mono text-xs text-slate-700">{e.id}</td>
                    <td className="py-3 pr-4 text-slate-800">
                      <div className="flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full flex-shrink-0" style={{ background: categoryColor[e.category] }} />
                        <span>{e.type}</span>
                      </div>
                      <div className="text-[10px] text-slate-500 mt-0.5 ml-4">{e.category}</div>
                    </td>
                    <td className="py-3 pr-4">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border ${statusStyles[e.status]}`}>
                        {e.status}
                      </span>
                    </td>
                    <td className="py-3 pr-4 text-slate-700 text-xs">
                      {isHrs ? `${fmt(e.lifeHrs)} hrs` : `${fmt(e.lifeKm)} km`}
                    </td>
                    <td className="py-3 pr-4">
                      <div className="flex items-center gap-2">
                        <div className="w-20 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full bg-slate-900" style={{ width: `${e.uptime}%` }} />
                        </div>
                        <span className="text-xs text-slate-700 font-medium w-10">{e.uptime ? `${e.uptime}%` : "—"}</span>
                      </div>
                    </td>
                    <td className="py-3 pr-4">
                      <div className="flex items-center gap-1.5">
                        <span className="font-display text-sm font-bold text-slate-900">{e.healthScore}</span>
                        <div className={`h-1.5 w-1.5 rounded-full ${e.healthScore >= 85 ? "bg-emerald-500" : e.healthScore >= 70 ? "bg-amber-500" : "bg-red-500"}`} />
                      </div>
                    </td>
                    <td className="py-3 pr-4 text-slate-600 text-xs">{e.nextService}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </SectionCard>

      {/* Bottlenecks */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
        {bottlenecks.map((b, i) => (
          <div key={i} className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm" data-testid={`bottleneck-${i}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <AlertTriangle size={16} className={b.impact === "High" ? "text-red-600" : "text-amber-600"} />
                <span className={`text-[10px] font-bold uppercase tracking-wider ${b.impact === "High" ? "text-red-600" : "text-amber-700"}`}>
                  {b.impact} impact
                </span>
              </div>
              <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-slate-900 text-amber-400 text-[10px] font-bold uppercase tracking-wider">
                {b.financialImpact}
              </span>
            </div>
            <h4 className="font-display text-base font-semibold text-slate-900">{b.title}</h4>
            <p className="text-sm text-slate-600 mt-2 leading-relaxed">{b.note}</p>
          </div>
        ))}
      </div>

      <AssetDetailDrawer asset={selectedAsset} onClose={() => setSelectedAsset(null)} />
    </div>
  );
}

function EsgStat({ label, value, sub, tone }) {
  const valueClass =
    tone === "emerald" ? "text-emerald-700" :
    tone === "amber" ? "text-amber-700" :
    "text-slate-900";
  return (
    <div className="bg-slate-50 border border-slate-100 rounded-md p-3">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className={`font-display text-xl font-bold mt-1 ${valueClass}`}>{value}</div>
      <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>
    </div>
  );
}
